import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    CheckCircle as FinalizeIcon,
    CloudDownload as DownloadIcon,
    Send as ShareIcon,
    Inventory as InventoryIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiClient from '../../../api/client';

interface GRNItem {
    id: number;
    item_name: string;
    gross_received: number;
    receiving_damage: number;
    receiving_reject: number;
    grade_a_qty: number;
    grade_b_qty: number;
    grade_c_qty: number;
    qc_wastage_qty: number;
    final_accepted: number;
    total_wastage: number;
}

interface GRNDetails {
    id: number;
    grn_number: string;
    po_number: string;
    receiving_number: string;
    batch_number: string;
    vendor_name: string;
    status: string;
    shared_with_farm: boolean;
    shared_with_accounts: boolean;
    created_at: string;
    items: GRNItem[];
}

const GRNDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [grn, setGRN] = useState<GRNDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
    const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/grn-new/${id}`);
            setGRN(response.data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch GRN details');
        } finally {
            setLoading(false);
        }
    };

    const handleFinalize = async () => {
        try {
            await apiClient.post(`/grn-new/${id}/finalize`);
            setFinalizeDialogOpen(false);
            enqueueSnackbar('GRN finalized successfully', { variant: 'success' });
            fetchDetails();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to finalize GRN', { variant: 'error' });
        }
    };

    const handleShareWithFarm = async () => {
        try {
            const response = await apiClient.get(`/grn-new/${id}/share-with-farm`, {
                responseType: 'blob'
            });

            // Download PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `GRN-${grn?.grn_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            fetchDetails();
        } catch (err: any) {
            enqueueSnackbar('Failed to generate PDF', { variant: 'error' });
        }
    };

    const handleShareWithAccounts = async () => {
        try {
            await apiClient.post(`/grn-new/${id}/share-with-accounts`);
            enqueueSnackbar('Shared with accounts successfully', { variant: 'success' });
            fetchDetails();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to share with accounts', { variant: 'error' });
        }
    };

    const handleSendToInventory = async () => {
        try {
            await apiClient.post(`/grn-new/${id}/send-to-inventory`);
            setInventoryDialogOpen(false);
            enqueueSnackbar('GRN sent to inventory successfully!', { variant: 'success' });
            navigate('/inventory');
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.detail || 'Failed to send to inventory', { variant: 'error' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !grn) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || 'GRN not found'}</Alert>
            </Box>
        );
    }

    const isDraft = grn.status === 'draft';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/inward/grn-new')}>
                        <BackIcon />
                    </IconButton>
                    <Typography variant="h4">GRN - {grn.grn_number}</Typography>
                    <Chip label={grn.status.toUpperCase()} color={isDraft ? 'default' : 'success'} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isDraft && (
                        <Button
                            variant="contained"
                            startIcon={<FinalizeIcon />}
                            onClick={() => setFinalizeDialogOpen(true)}
                        >
                            Finalize
                        </Button>
                    )}
                    {!isDraft && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<InventoryIcon />}
                                onClick={() => setInventoryDialogOpen(true)}
                            >
                                Send to Inventory
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleShareWithFarm}
                            >
                                Share with Farm
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ShareIcon />}
                                onClick={handleShareWithAccounts}
                                disabled={grn.shared_with_accounts}
                            >
                                {grn.shared_with_accounts ? 'Shared with Accounts' : 'Share with Accounts'}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>GRN Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">PO Number</Typography>
                                    <Typography variant="body1">{grn.po_number}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">Receiving Number</Typography>
                                    <Typography variant="body1">{grn.receiving_number}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">Batch Number</Typography>
                                    <Typography variant="body1">{grn.batch_number}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">Vendor</Typography>
                                    <Typography variant="body1">{grn.vendor_name}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" color="text.secondary">Created</Typography>
                                    <Typography variant="body1">
                                        {format(new Date(grn.created_at), 'dd MMM yyyy HH:mm')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Items Summary</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Combined data from Receiving and Grading
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Gross Received</TableCell>
                                    <TableCell align="right">Damage</TableCell>
                                    <TableCell align="right">Reject</TableCell>
                                    <TableCell align="right">Grade A</TableCell>
                                    <TableCell align="right">Grade B</TableCell>
                                    <TableCell align="right">Grade C</TableCell>
                                    <TableCell align="right">QC Wastage</TableCell>
                                    <TableCell align="right">Final Accepted</TableCell>
                                    <TableCell align="right">Total Wastage</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grn.items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.item_name}</TableCell>
                                        <TableCell align="right">{item.gross_received}</TableCell>
                                        <TableCell align="right">{item.receiving_damage}</TableCell>
                                        <TableCell align="right">{item.receiving_reject}</TableCell>
                                        <TableCell align="right">{item.grade_a_qty}</TableCell>
                                        <TableCell align="right">{item.grade_b_qty}</TableCell>
                                        <TableCell align="right">{item.grade_c_qty}</TableCell>
                                        <TableCell align="right">{item.qc_wastage_qty}</TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="bold" color="success.main">
                                                {item.final_accepted}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography color="error.main">
                                                {item.total_wastage}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Finalize Dialog */}
            <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)}>
                <DialogTitle>Finalize GRN?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will:
                        • Update the PO with actual quantities
                        • Lock the GRN from further edits
                        • Mark the GRN as ready for sharing
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleFinalize} variant="contained">
                        Finalize
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send to Inventory Dialog */}
            <Dialog open={inventoryDialogOpen} onClose={() => setInventoryDialogOpen(false)}>
                <DialogTitle>Send to Inventory?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will skip Packing & Labeling and send items directly to inventory:
                        <br />• Create inventory records for Grade A, B, C quantities
                        <br />• Update batch status to 'in_inventory'
                        <br />• Items will go straight to warehouse
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInventoryDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendToInventory} variant="contained" color="primary">
                        Send to Inventory
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GRNDetailPage;
